/**
 * Demo-mode extras for the visitor details panel: what a visitor-day carries
 * beyond the visit row (admission, questionnaire halves, spaces, comments,
 * activity). Spec sample data: `concierge-desktop/screens/visitor-details.md`.
 *
 * Priya Nadar (`v-priya`) is the hero, blocked at the desk. `ADMISSION_PREVIEWS`
 * replays her day in each admission state for `/preview/visit?admission=…`.
 * Henrik Larsson (`v-henrik`) is the multi-day visit, day 2 of 3, with day 3
 * blocked by an insurance certificate that lapses first.
 *
 * Comments with authors and host whereabouts are not served by the API yet
 * (`NEEDS_API`); the demo shows them so the design can be reviewed. Spaces
 * are served (`VisitItem.allocations`); the demo's sit on the sample floor.
 */

import {
  PHOTO_ROW_ID,
  type AdmissionKey,
  type RequirementRow,
} from '../visits/visit-detail-panel/admission';
import type { VisitorDay } from '../visits/visit-detail-panel/visitor-day';
import { demoUpload } from './demo-uploads';

/** Local date-time ISO for today+`dayOffset` at `hh:mm`. */
function at(dayOffset: number, hh: number, mm = 0): string {
  const d = new Date();
  d.setDate(d.getDate() + dayOffset);
  d.setHours(hh, mm, 0, 0);
  return d.toISOString();
}

/** Local `YYYY-MM-DD` for today+`dayOffset`. */
function ymd(dayOffset: number): string {
  const d = new Date();
  d.setDate(d.getDate() + dayOffset);
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
}

function short(dayOffset: number): string {
  const d = new Date();
  d.setDate(d.getDate() + dayOffset);
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

/** "Thu 16 Jul" for today+`dayOffset`, as the space details read it. */
function day(dayOffset: number): string {
  const d = new Date();
  d.setDate(d.getDate() + dayOffset);
  return d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
}

type Extras = Partial<Omit<VisitorDay, 'visit'>>;

const PRIYA_ROWS = {
  induction: {
    id: 'req-induction',
    label: 'Site safety induction',
    mark: 'expired',
    blocking: true,
    detail: 'Expired 11 Jan · redo at the kiosk',
    action: null,
  },
  insurance: {
    id: 'req-insurance',
    label: 'Public liability insurance',
    mark: 'review',
    blocking: true,
    detail: `Uploaded ${short(-1)} · waiting for review`,
    action: 'review',
    upload: demoUpload('Public liability insurance', 'Priya Nadar'),
  },
  photoId: {
    id: 'req-photo-id',
    label: 'Photo ID',
    mark: 'met',
    blocking: true,
    detail: `Seen ${short(-1)}`,
    action: null,
  },
  questionnaire: {
    id: 'req-hs',
    label: 'Health and safety questionnaire',
    mark: 'met',
    blocking: true,
    detail: `Answered ${short(-1)}`,
    action: null,
  },
  hotWorks: {
    id: 'req-hot-works',
    label: 'Hot works permit',
    mark: 'missing',
    blocking: false,
    detail: 'Not seen · optional',
    action: 'markSeen',
  },
} satisfies Record<string, RequirementRow>;

const INDUCTION_MET: RequirementRow = {
  ...PRIYA_ROWS.induction,
  mark: 'met',
  detail: `Completed ${short(-14)}`,
};
const INSURANCE_MET: RequirementRow = {
  ...PRIYA_ROWS.insurance,
  mark: 'met',
  detail: `Reviewed ${short(-1)}`,
  action: null,
};
const ALL_MET = [
  PRIYA_ROWS.photoId,
  PRIYA_ROWS.questionnaire,
  INDUCTION_MET,
  INSURANCE_MET,
  PRIYA_ROWS.hotWorks,
];

export const DEMO_VISITOR_DAYS: Readonly<Record<string, Extras & DemoOnly>> = {
  'v-priya': {
    attendanceId: 'att-priya-0',
    status: 'expected',
    requirements: Object.values(PRIYA_ROWS),
    photoOwed: false,
    questionnaires: {
      before: { answered: 6, total: 6, completedAt: at(-1, 18, 4) },
      onArrival: { answered: 2, total: 4, completedAt: null },
    },
    site: { name: 'Birmingham Rum Runner Works', campus: 'Birmingham campus' },
    pin: '4821',
    spaces: [
      {
        id: 'sp-desk',
        kind: 'desk',
        name: 'Desk 26',
        detail: 'Ground floor · 08:30–13:00',
        when: `${day(0)} · 08:30–13:00`,
        times: '08:30–13:00',
        where: 'Ground floor · Open workspace',
        building: 'Birmingham Rum Runner Works',
        image: null,
        amenities: [
          { key: 'monitor', label: 'Monitor' },
          { key: 'height-adjustable', label: 'Height adjustable' },
          { key: 'usb-c-dock', label: 'USB-C dock' },
          { key: 'power', label: 'Power outlets' },
        ],
        status: 'held',
        reason: null,
        // The sample floor (public/demo/floor-plans): desk 26 by the Collaboration Area.
        map: {
          locationId: 'loc-rrw',
          floorId: 'floor-bham-ground',
          link: { spaceId: 'demo-desk-26' },
        },
      },
      {
        id: 'sp-park',
        kind: 'parking',
        name: 'Bay 14',
        detail: 'Car park B · All day · MF21 KXP',
        when: `${day(0)} · All day`,
        times: 'All day',
        where: 'Car park B · Level -1',
        building: 'Birmingham Rum Runner Works',
        image: null,
        amenities: [
          { key: 'ev-charger', label: 'EV charger · Type 2' },
          { key: 'clearance', label: 'Clearance 2.1 m' },
        ],
        status: 'held',
        reason: null,
        // A mock car park (public/demo/floor-plans): car parks rarely have a plan yet.
        map: {
          locationId: 'loc-rrw',
          floorId: 'floor-bham-car-park-b',
          link: { spaceId: 'demo-bay-14' },
        },
      },
    ],
    hostWhereabouts: { 'h-marcus': 'Hopper House, desk 4.12' },
    comments: [
      {
        id: 'c-1',
        author: 'Marcus Webb',
        role: 'Host',
        at: at(-2, 9, 34),
        body: 'Needs escorting to the roof plant room. Keys are with Facilities on level 1.',
      },
      {
        id: 'c-2',
        author: 'Sam Patel',
        role: 'Reception',
        at: at(0, 8, 38),
        body: 'Insurance certificate still missing. Asked Priya to email it.',
      },
    ],
    extraActivity: [
      {
        id: 'e-upload',
        at: at(-1, 17, 52),
        actor: 'Priya Nadar',
        actorKind: 'visitor',
        text: $localize`:@@visitors-activity-uploaded:uploaded an insurance certificate`,
        icon: 'badge-check',
      },
      {
        id: 'e-q',
        at: at(-1, 18, 4),
        actor: 'Priya Nadar',
        actorKind: 'visitor',
        text: $localize`:@@visitors-activity-answered:completed the questionnaire`,
        icon: 'clipboard-check',
      },
      {
        id: 'e-announce',
        at: at(0, 8, 30),
        actor: 'Priya Nadar',
        actorKind: 'visitor',
        text: $localize`:@@visitors-activity-announced:announced at the kiosk`,
        icon: 'smartphone',
      },
      {
        id: 'e-refused',
        at: at(0, 8, 41),
        actor: 'Sam Patel',
        actorKind: 'staff',
        text: $localize`:@@visitors-activity-tried-sign-in:tried to sign them in`,
        icon: 'shield-x',
        note: 'Insurance certificate and site induction missing.',
        noteTag: $localize`:@@visitors-activity-sign-in-refused:Sign-in refused`,
        noteTone: 'error',
      },
    ],
  },
  'v-henrik': {
    attendanceId: 'att-henrik-1',
    status: 'onSite',
    arrivedAt: at(0, 8, 52),
    days: [
      { date: ymd(-1), attendanceId: 'att-henrik-0', status: 'checkedOut', blocked: false },
      { date: ymd(0), attendanceId: 'att-henrik-1', status: 'onSite', blocked: false },
      { date: ymd(1), attendanceId: 'att-henrik-2', status: 'expected', blocked: true },
    ],
    dayIndex: 1,
    requirements: [
      {
        id: 'req-nda',
        label: 'Signed NDA',
        mark: 'met',
        blocking: true,
        detail: `Signed ${short(-5)}`,
        action: null,
      },
      {
        id: 'req-photo-id',
        label: 'Photo ID',
        mark: 'met',
        blocking: true,
        detail: `Seen ${short(-1)}`,
        action: null,
      },
      {
        id: 'req-insurance',
        label: 'Public liability insurance',
        mark: 'expiring',
        blocking: true,
        detail: `Expires ${short(0)}`,
        action: null,
      },
    ],
    site: { name: 'Speechmark', campus: 'Birmingham campus' },
    // Speechmark has no plan of its own: the desk sits on the sample floor,
    // as the design assumes every space has a map (Rahim, 23 Oct).
    spaces: [
      {
        id: 'sp-henrik-desk',
        kind: 'desk',
        name: 'Desk 12',
        detail: 'Level 2 · 08:30–16:30',
        when: `${day(0)} · 08:30–16:30`,
        times: '08:30–16:30',
        where: 'Level 2',
        building: 'Speechmark',
        image: null,
        amenities: [{ key: 'monitor', label: 'Monitor' }],
        status: 'held',
        reason: null,
        map: {
          locationId: 'loc-spm',
          floorId: 'floor-bham-ground',
          link: { spaceId: 'demo-desk-12' },
        },
      },
    ],
    comments: [],
  },
};

/** Fields the demo store keeps beside a visitor-day, folded in by the service. */
export interface DemoOnly {
  readonly hostWhereabouts?: Readonly<Record<string, string>>;
  readonly extraActivity?: VisitorDay['activity'];
}

/** Priya's day in each admission state (`/preview/visit?admission=…`). */
export const ADMISSION_PREVIEWS: Readonly<Record<AdmissionKey, Extras>> = {
  ready: { status: 'expected', requirements: ALL_MET },
  expiring: {
    status: 'expected',
    expiringInDays: 3,
    requirements: [
      { ...INSURANCE_MET, mark: 'expiring', detail: `Expires ${short(3)}` },
      PRIYA_ROWS.photoId,
      PRIYA_ROWS.questionnaire,
      INDUCTION_MET,
      PRIYA_ROWS.hotWorks,
    ],
  },
  missing: {},
  photo: {
    status: 'expected',
    photoOwed: true,
    requirements: [
      {
        id: PHOTO_ROW_ID,
        label: 'Photo',
        mark: 'missing',
        blocking: true,
        detail: 'Needed · take it at the kiosk',
        action: null,
      },
      ...ALL_MET,
    ],
  },
  signingIn: { status: 'checkingIn', requirements: ALL_MET },
  signedIn: { status: 'onSite', arrivedAt: at(0, 8, 43), requirements: ALL_MET },
  override: {
    status: 'onSite',
    arrivedAt: at(0, 8, 47),
    override: {
      by: 'Sam Patel',
      at: at(0, 8, 47),
      reason: 'Host escorting all day, certificate to follow',
    },
    requirements: [
      { ...PRIYA_ROWS.induction, detail: 'Expired 11 Jan · overridden' },
      {
        ...PRIYA_ROWS.insurance,
        mark: 'missing',
        detail: 'Not reviewed · overridden',
        action: null,
      },
      PRIYA_ROWS.photoId,
      PRIYA_ROWS.questionnaire,
    ],
  },
  signedOut: {
    status: 'checkedOut',
    arrivedAt: at(0, 8, 43),
    departedAt: at(0, 17, 2),
    requirements: ALL_MET,
  },
  didntArrive: {
    status: 'noShow',
    requirements: [
      PRIYA_ROWS.photoId,
      PRIYA_ROWS.questionnaire,
      { ...PRIYA_ROWS.induction, mark: 'missing', detail: 'Not completed' },
      { ...PRIYA_ROWS.insurance, mark: 'missing', detail: 'Not uploaded', action: null },
    ],
  },
};
